const TYPE_PRIORITY = { place: 0, locality: 1, neighborhood: 2, address: 3 };

/**
 * Fetch geocoding suggestions from Mapbox Geocoding API v5.
 * Returns normalized results scoped to the US, sorted by type priority
 * (regions/states first, then places, localities, etc.).
 *
 * @param {string} query - Search text (>= 2 chars recommended)
 * @param {AbortSignal} [signal] - Optional AbortSignal for cancellation
 * @returns {Promise<Array<{ name: string, fullName: string, lat: number, lng: number, placeType: string }>>}
 */
export async function fetchGeocodingSuggestions(query, signal) {
    const token = import.meta.env.VITE_MAPBOX_TOKEN || '';
    if (!token || !query || query.length < 2) return [];

    const encoded = encodeURIComponent(query.trim());
    // bbox covers Florida + Alabama
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${token}&country=us&types=place,locality,neighborhood&limit=10&autocomplete=true&bbox=-88.5,24.4,-79.8,35.0`;

    const response = await fetch(url, { signal });

    if (!response.ok) return [];

    const data = await response.json();

    if (!data.features?.length) return [];

    return data.features
        .map((feature) => ({
            name: feature.text,
            fullName: feature.place_name.replace(/,\s*(Florida|Alabama),\s*United States$/, '').replace(/,\s*United States$/, ''),
            lat: feature.center[1],
            lng: feature.center[0],
            placeType: feature.place_type?.[0] || 'place',
        }))
        .sort((a, b) => (TYPE_PRIORITY[a.placeType] ?? 5) - (TYPE_PRIORITY[b.placeType] ?? 5));
}
